import { Search } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'

import { paths } from 'src/config/paths'
import { cn } from 'src/lib/utils'
import { TriangleFilled } from 'src/shared/components/icons/triangle-filled'
import { useIsMobile } from 'src/shared/hooks/use-mobile'

import { professionMap } from '../../schemas/profession'
import useActiveFilterStore from '../../stores/marketplace/active-filter'

import { useLocationInputRef } from './context/use-location-input'

const MarketplaceTitle = () => {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const locationInputRef = useLocationInputRef()
  const { setActiveDropdown, submittedFilters, isUsingExactLocation } = useActiveFilterStore(
    useShallow((state) => ({
      setActiveDropdown: state.setActiveDropdown,
      submittedFilters: state.submittedFilters,
      isUsingExactLocation: state.isUsingExactLocation,
    })),
  )

  const onSelectProfession = () => {
    if (isMobile) {
      navigate(paths.marketplace.filters.path)
      setActiveDropdown('profession')
    } else {
      setActiveDropdown('profession')
    }
  }

  const onSelectLocation = () => {
    if (isMobile) {
      navigate(paths.marketplace.filters.path)
      setActiveDropdown('where')
    } else {
      setActiveDropdown('where')
      locationInputRef.current?.focus()
    }
  }

  const locationText = useMemo(() => {
    if (isUsingExactLocation) {
      return 'your area'
    }
    if (submittedFilters.location) {
      return submittedFilters.location
    }
    return 'the United States'
  }, [isUsingExactLocation, submittedFilters.location])

  const description = useMemo(() => {
    return `Verified & trusted ${
      submittedFilters.profession
        ? professionMap[submittedFilters.profession]?.label || 'real estate professionals'
        : 'real estate professionals'
    } in ${locationText} available to connect with for your next real estate transaction`
  }, [submittedFilters.profession, locationText])

  const title = useMemo(() => {
    const profession = submittedFilters.profession
      ? professionMap[submittedFilters.profession]?.label || 'real estate professional'
      : 'real estate professional'

    return `Find the best ${profession} in ${locationText} | Conrad`
  }, [submittedFilters.profession, locationText])

  return (
    <div className="pb-6 pt-2">
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:description" content={description} />
      <meta name="twitter:description" content={description} />
      <h1
        className={cn(
          'flex flex-wrap items-center text-[22px] font-bold leading-[2.0] text-muted lg:text-[26px]',
          isMobile ? 'text-center' : '',
        )}
      >
        Find the best <span className="w-4" />
        <span
          onClick={onSelectProfession}
          className="mr-4 inline-flex h-auto cursor-pointer items-center whitespace-nowrap rounded-full px-6 py-2 leading-[1.2] text-primary shadow-md"
          style={{
            border: '0.5px solid #D8D8D6',
            boxShadow: '0px 0px 6px 0px rgba(27, 48, 41, 0.10)',
          }}
        >
          {submittedFilters.profession
            ? professionMap[submittedFilters.profession]?.label || 'Professional'
            : 'Professional'}
          <TriangleFilled className="ml-4 inline-block size-[13px] rotate-180 text-muted lg:size-[15px]" />
        </span>
        in <span className="w-4" />
        <span
          onClick={onSelectLocation}
          className="mr-4 inline-flex cursor-pointer items-center whitespace-nowrap rounded-full px-6 py-2 leading-[1.2] text-primary shadow-md"
          style={{
            border: '0.5px solid #D8D8D6',
            boxShadow: '0px 0px 6px 0px rgba(27, 48, 41, 0.10)',
          }}
        >
          <Search className="mr-3 inline-block size-[21px] text-muted lg:size-[25px]" />
          {locationText}
        </span>
      </h1>
    </div>
  )
}

export default MarketplaceTitle
